import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {QuoteDto} from "../shell-communication/dtos/quote-dto.class";

/**
 * QuoteLatencyService - Angular 8.0 Implementation
 *
 * Measures latency between quote subscription requests and actual quote delivery.
 * Tracks only the first request and first quote for each ticker until unsubscribed.
 */

export interface LatencyMeasurement {
    ticker: string;
    latencyMs: number;
    requestTime: number;
    receiveTime: number;
}

export interface LatencyStats {
    count: number;
    avgMs: number;
    minMs: number;
    maxMs: number;
    p50Ms: number;
    p95Ms: number;
    p99Ms: number;
}

export interface LatencyServiceConfig {
    historySize?: number;
    enableLogging?: boolean;
    warnThresholdMs?: number;
}

export interface DebugInfo {
    pendingCount: number;
    activeSubscriptions: number;
    historyCount: number;
    historyCapacity: number;
}

@Injectable({
    providedIn: 'root'
})
export class QuoteLatencyService {
    private readonly config: Required<LatencyServiceConfig>;

    // Track pending requests (waiting for first quote)
    private pendingRequests: Map<string, number> = new Map();

    // Track active subscriptions (to enforce first-request-only rule)
    private activeSubscriptions: Set<string> = new Set();

    // Circular buffer for latency measurements
    private latencyHistory: LatencyMeasurement[] = [];
    private historyWriteIndex: number = 0;

    // Optional: cache latest latency per ticker for quick lookup
    private latestLatencyCache: Map<string, number> = new Map();

    // Observables for reactive updates
    private statsSubject$ = new BehaviorSubject<LatencyStats>(this.getEmptyStats());
    private pendingCountSubject$ = new BehaviorSubject<number>(0);
    private latestMeasurementSubject$ = new BehaviorSubject<LatencyMeasurement | null>(null);

    // Public observables
    public stats$: Observable<LatencyStats> = this.statsSubject$.asObservable();
    public pendingCount$: Observable<number> = this.pendingCountSubject$.asObservable();
    public latestMeasurement$: Observable<LatencyMeasurement | null> = this.latestMeasurementSubject$.asObservable();

    constructor() {
        this.config = {
            historySize: 1000,
            enableLogging: false,
            warnThresholdMs: 1000,
        };
    }

    /**
     * Configure the service (call before using)
     */
    public configure(config: LatencyServiceConfig): void {
        if (config.historySize !== undefined) {
            this.config.historySize = config.historySize;
        }
        if (config.enableLogging !== undefined) {
            this.config.enableLogging = config.enableLogging;
        }
        if (config.warnThresholdMs !== undefined) {
            this.config.warnThresholdMs = config.warnThresholdMs;
        }
    }

    /**
     * Record quote request for a batch of tickers.
     * Only records first request per ticker (ignores duplicates while subscribed).
     */
    public recordRequest(tickers: string[]): void {
        const timestamp = performance.now();
        let recordedCount = 0;

        for (const ticker of tickers) {
            // Only record if this ticker is not already actively subscribed
            if (!this.activeSubscriptions.has(ticker)) {
                this.pendingRequests.set(ticker, timestamp);
                this.activeSubscriptions.add(ticker);
                recordedCount++;
            }
        }

        // Emit updated pending count
        this.pendingCountSubject$.next(this.pendingRequests.size);

        if (this.config.enableLogging && recordedCount > 0) {
            console.log(`[LatencyService] Recorded ${recordedCount} new requests from batch of ${tickers.length}`);
        }
    }

    /**
     * Process incoming quotes and calculate latency for pending requests.
     * Only measures latency for first quote occurrence per ticker.
     */
    public onQuoteMessage(quotes: QuoteDto[]): void {
        const receiveTime = performance.now();
        let measuredCount = 0;
        let lastMeasurement: LatencyMeasurement | null = null;

        for (const quote of quotes) {
            const ticker = quote.ticker;

            // Only measure if we have a pending request for this ticker
            if (this.pendingRequests.has(ticker)) {
                const requestTime = this.pendingRequests.get(ticker)!;
                const latencyMs = receiveTime - requestTime;

                // Create measurement
                const measurement: LatencyMeasurement = {
                    ticker,
                    latencyMs,
                    requestTime,
                    receiveTime,
                };

                // Store measurement
                this.addMeasurement(measurement);
                lastMeasurement = measurement;

                // Remove from pending (we only measure first occurrence)
                this.pendingRequests.delete(ticker);

                // Update cache
                this.latestLatencyCache.set(ticker, latencyMs);

                measuredCount++;

                // Log warning if latency exceeds threshold
                if (this.config.enableLogging && latencyMs > this.config.warnThresholdMs) {
                    console.warn(`[LatencyService] High latency detected for ${ticker}: ${latencyMs.toFixed(2)}ms`);
                }
            }
        }

        if (measuredCount > 0) {
            // Emit updated stats and pending count
            this.statsSubject$.next(this.getLatencyStats());
            this.pendingCountSubject$.next(this.pendingRequests.size);

            // Emit latest measurement
            if (lastMeasurement) {
                this.latestMeasurementSubject$.next(lastMeasurement);
            }
        }

        if (this.config.enableLogging && measuredCount > 0) {
            console.log(`[LatencyService] Measured ${measuredCount} quotes from batch of ${quotes.length}`);
        }
    }

    /**
     * Unsubscribe tickers - removes from active tracking but preserves measurements.
     */
    public unsubscribe(tickers: string[]): void {
        for (const ticker of tickers) {
            this.pendingRequests.delete(ticker);
            this.activeSubscriptions.delete(ticker);
        }

        // Emit updated pending count
        this.pendingCountSubject$.next(this.pendingRequests.size);

        if (this.config.enableLogging) {
            console.log(`[LatencyService] Unsubscribed ${tickers.length} tickers`);
        }
    }

    /**
     * Get comprehensive latency statistics from measurement history.
     */
    public getLatencyStats(): LatencyStats {
        const measurements = this.getAllMeasurements();

        if (measurements.length === 0) {
            return this.getEmptyStats();
        }

        const latencies = measurements.map(m => m.latencyMs).sort((a, b) => a - b);
        const sum = latencies.reduce((acc, val) => acc + val, 0);

        return {
            count: latencies.length,
            avgMs: sum / latencies.length,
            minMs: latencies[0],
            maxMs: latencies[latencies.length - 1],
            p50Ms: this.getPercentile(latencies, 0.50),
            p95Ms: this.getPercentile(latencies, 0.95),
            p99Ms: this.getPercentile(latencies, 0.99),
        };
    }

    /**
     * Get the most recent latency measurement for a specific ticker.
     * Returns null if no measurement exists.
     */
    public getLatencyForTicker(ticker: string): number | null {
        return this.latestLatencyCache.get(ticker) ?? null;
    }

    /**
     * Get count of pending requests (quotes requested but not yet received).
     */
    public getPendingCount(): number {
        return this.pendingRequests.size;
    }

    /**
     * Get list of tickers with pending requests.
     */
    public getPendingTickers(): string[] {
        return Array.from(this.pendingRequests.keys());
    }

    /**
     * Get count of active subscriptions.
     */
    public getActiveSubscriptionCount(): number {
        return this.activeSubscriptions.size;
    }

    /**
     * Clear measurement history but keep pending requests and subscriptions.
     */
    public clearHistory(): void {
        this.latencyHistory = [];
        this.historyWriteIndex = 0;
        this.latestLatencyCache.clear();

        // Emit updated stats
        this.statsSubject$.next(this.getEmptyStats());

        if (this.config.enableLogging) {
            console.log('[LatencyService] Cleared measurement history');
        }
    }

    /**
     * Reset everything - pending requests, subscriptions, and history.
     */
    public reset(): void {
        this.pendingRequests.clear();
        this.activeSubscriptions.clear();
        this.latencyHistory = [];
        this.historyWriteIndex = 0;
        this.latestLatencyCache.clear();

        // Emit reset state
        this.statsSubject$.next(this.getEmptyStats());
        this.pendingCountSubject$.next(0);
        this.latestMeasurementSubject$.next(null);

        if (this.config.enableLogging) {
            console.log('[LatencyService] Full reset completed');
        }
    }

    /**
     * Get all measurements in chronological order (oldest to newest).
     */
    public getAllMeasurements(): LatencyMeasurement[] {
        if (this.latencyHistory.length < this.config.historySize) {
            // Buffer not full yet, return in order
            return [...this.latencyHistory];
        } else {
            // Buffer is full, need to reorder from write index
            const older = this.latencyHistory.slice(this.historyWriteIndex);
            const newer = this.latencyHistory.slice(0, this.historyWriteIndex);
            return [...older, ...newer];
        }
    }

    /**
     * Get debug information about service state.
     */
    public getDebugInfo(): DebugInfo {
        return {
            pendingCount: this.pendingRequests.size,
            activeSubscriptions: this.activeSubscriptions.size,
            historyCount: this.latencyHistory.length,
            historyCapacity: this.config.historySize,
        };
    }

    /**
     * Add a measurement to the circular buffer.
     */
    private addMeasurement(measurement: LatencyMeasurement): void {
        if (this.latencyHistory.length < this.config.historySize) {
            // Buffer not full yet, just append
            this.latencyHistory.push(measurement);
        } else {
            // Buffer full, overwrite oldest entry
            this.latencyHistory[this.historyWriteIndex] = measurement;
        }

        // Move write pointer (circular)
        this.historyWriteIndex = (this.historyWriteIndex + 1) % this.config.historySize;
    }

    /**
     * Calculate percentile from sorted array.
     */
    private getPercentile(sortedArray: number[], percentile: number): number {
        if (sortedArray.length === 0) {
            return 0;
        }

        const index = Math.ceil(sortedArray.length * percentile) - 1;
        return sortedArray[Math.max(0, index)];
    }

    /**
     * Get empty stats structure.
     */
    private getEmptyStats(): LatencyStats {
        return {
            count: 0,
            avgMs: 0,
            minMs: 0,
            maxMs: 0,
            p50Ms: 0,
            p95Ms: 0,
            p99Ms: 0,
        };
    }
}